import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { loginRequest } from 'B2C';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { msalInstance } from 'msalInstance';
import { getAppAccessToken } from 'Utils/appAccessToken';
import { getCurrentAuthToken } from 'Utils/getCurrentAuthToken';
import { v4 as uuidv4 } from 'uuid';
import { checkRepresentativeRestrictions, isRepresentativesAllowedPath } from './lawyerAccess';

export {
  USER_TYPE,
  representativesAllowedUrls,
  checkRepresentativeRestrictions,
  isRepresentativesAllowedPath,
  isLawyerAndShikumUser,
  isLawyerLobbyPath,
  needsContactDetailsOnboarding,
  tokenIncludesUserRanks,
} from './lawyerAccess';

interface ErrorResponse {
  message: string;
}

export const nonB2CUrls = ['/universalRequest', '/emergency-call/login'];

const timeoutManager = (() => {
  let requestsTimeOutId: number | null = null;
  return {
    set: (timeoutId: number) => {
      if (requestsTimeOutId) clearTimeout(requestsTimeOutId);
      requestsTimeOutId = timeoutId;
    },
    clear: () => {
      if (requestsTimeOutId) clearTimeout(requestsTimeOutId);
      requestsTimeOutId = null;
    },
  };
})();

export const api = axios.create({
  baseURL: import.meta.env.VITE_APP_DOMAIN,
  allowAbsoluteUrls: false,
});

const notAuthAction = async () => {
  if (getAppAccessToken()) {
    return;
  }
  if (window.location.pathname.includes('emergency-call')) {
    window.location.href = '/emergency-call/login';
    return;
  }
  if (window.location.pathname.toLowerCase().includes('universalRequest'.toLocaleLowerCase())) {
    return;
  }
  if (window.location.pathname.toLowerCase().includes('/suppliers')) {
    const returnUrl = window.location.pathname + window.location.search;
    const bridgeUrl = `/auth-bridge?returnUrl=${encodeURIComponent(returnUrl)}`;
    await msalInstance.loginRedirect({
      ...loginRequest,
      redirectStartPage: window.location.origin + bridgeUrl,
    });
    return;
  }
  await msalInstance.loginRedirect(loginRequest);
};

async function handleAxiosError(error: AxiosError<ErrorResponse>, options: { isResponse?: boolean } = {}) {
  const token = getCurrentAuthToken();
  const status = error.response?.status;

  if (status === HTTP_STATUS_CODES.Unauthorized || (options.isResponse && (error as any).correlationId)) {
    await notAuthAction();
  } else if (status === HTTP_STATUS_CODES.Forbidden) {
    if (checkRepresentativeRestrictions(token) && !isRepresentativesAllowedPath(window.location.pathname)) {
      return Promise.reject(error);
    }
    timeoutManager.set(
      window.setTimeout(() => {
        alert(error.response?.data?.message || 'Forbidden access');
      }, 1000)
    );
  } else if (status === HTTP_STATUS_CODES.InternalServerError) {
    if (!(window.location.pathname.toLowerCase().includes('universalrequest') && (error.response?.data as any)?.tryAgain === true)) {
      window.location.href = '/error';
    }
  }
  return Promise.reject(error);
}

api.interceptors.request.use(
  async function (config: InternalAxiosRequestConfig) {
    const activeAccount = msalInstance.getActiveAccount();
    const allAccounts = msalInstance.getAllAccounts();
    const account = activeAccount || allAccounts[0] || undefined;

    const request = {
      scopes: loginRequest.scopes,
      account: account,
    };
    let token: string | null;
    const skipMsal = Boolean(getAppAccessToken()) || nonB2CUrls.some((url) => window.location.pathname.includes(url));

    try {
      if (skipMsal) {
        token = getCurrentAuthToken();
      } else {
        const response = await msalInstance.acquireTokenSilent(request);
        token = response.accessToken;
      }
    } catch (error) {
      token = getCurrentAuthToken();
      console.error(error);
    }

    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['X-Correlation-ID'] = uuidv4();

    return config;
  },
  async function (error: AxiosError<ErrorResponse>) {
    return handleAxiosError(error);
  }
);

api.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error: AxiosError<ErrorResponse>) {
    const traceId = (error.response?.data as any)?.TraceId;
    if (traceId) {
      console.error(`[Correlation ID: ${traceId}]`, error);
    } else {
      console.error(error);
    }
    return handleAxiosError(error, { isResponse: true });
  }
);
